import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchContentFeedEntries } from '@sola/my-sdk/content-feed/requests.ts';
import { useSponsorships } from '@sola/supporter-sdk/hooks/commitments.ts';
import { usePaymentMethodsWithIssues } from '@sola/supporter-sdk/hooks/payment-methods.ts';
import { trackError } from '@sola/tracking/components/datalayer.tsx';
import { type ContentSource, type SuppressionContext, filterByMatch, filterByRecency, runSuppressionPipeline } from './content-feed.suppression.ts';
import type { ContentFeedEntryResolved, ContentFeedListResponse } from './content-feed.types.ts';
type FetchFn = (params: {
  cursor: string | null;
}) => Promise<ContentFeedListResponse>;
const defaultEnabledSources: Record<ContentSource, boolean> = {
  community: true,
  causes: true,
  'compassion.media': true,
  'causes-progress-updates': false
};

/**
 * Hook that manages the paginated content feed state with suppression logic.
 * Handles fetching entries, pagination cursor, loading state, and content suppression.
 *
 * @param enabledSources - Which content sources are enabled (configured via Contentful block settings).
 * @param fetchOverride - Optional fetch function override for testing/Storybook.
 * @param suppressionEnabled - Whether the suppression pipeline is active.
 */
export const useContentFeed = (enabledSources: Record<ContentSource, boolean> = defaultEnabledSources, fetchOverride?: FetchFn, suppressionEnabled = false) => {
  const {
    t,
    i18n
  } = useTranslation(['my']);
  const [rawEntries, setRawEntries] = useState<ContentFeedEntryResolved[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [isFeedLoading, setIsFeedLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);

  // Sponsorship commitments: resolve FCP IDs, participant IDs, and country codes
  const {
    sponsorships,
    isLoading: isSponsorshipsLoading,
    isError: isSponsorshipsError
  } = useSponsorships();
  const isCommitmentsReady = !isSponsorshipsLoading || isSponsorshipsError;

  // Derive unique FCP IDs from commitments.
  // FCP ID = first 6 chars of participant localId (e.g. "DR054002196" → "DR0540").
  const fcpIds = useMemo(() => [...new Set(sponsorships.map(c => c.participant?.localId?.slice(0, 6)).filter((id): id is string => !!id))], [sponsorships]);

  // Build a localId → preferredName map for banner text resolution
  const participantNameMap = useMemo(() => new Map(sponsorships.filter(c_0 => c_0.participant?.localId).map(c_1 => [c_1.participant!.localId!, c_1.participant?.names?.preferred || c_1.participant?.name || c_1.participant!.localId!])), [sponsorships]);

  // Financial standing: use the existing payment methods hook
  const {
    paymentMethodsWithIssues,
    isLoading: isPaymentDataLoading
  } = usePaymentMethodsWithIssues();
  const inGoodStanding = !isPaymentDataLoading && paymentMethodsWithIssues.length === 0;

  // Combined loading state: show skeleton until all dependencies resolve
  const isLoading = isFeedLoading || isPaymentDataLoading || !isCommitmentsReady;
  const locale = i18n.language;
  const fetchEntries: FetchFn = fetchOverride ? fetchOverride : params => fetchContentFeedEntries({
    ...params,
    locale,
    fcpIds,
    includeNearbyEntries: true,
    sortBy: 'proximity',
    sortDirection: 'desc',
    limit: 10
  });

  // Hardcoded test data for local testing:
  // const fetchEntries: FetchFn = fetchOverride
  //   ? fetchOverride
  //   : (params) =>
  //       fetchContentFeedEntries({
  //         ...params,
  //         locale,
  //         participantLocalIds: ['DR054002196'],
  //         includeNearbyEntries: true,
  //         sortBy: 'proximity',
  //         sortDirection: 'desc',
  //         limit: 10,
  //       });

  // Fetch entries once commitments are resolved (and re-fetch on locale change)
  useEffect(() => {
    if (!isCommitmentsReady) {
      return;
    }

    // Skip if no sponsored children resolved
    if (fcpIds.length === 0) {
      setRawEntries([]);
      setHasMore(false);
      setIsFeedLoading(false);
      return;
    }
    let cancelled = false;
    setIsFeedLoading(true);
    fetchEntries({
      cursor: null
    }).then(response => {
      if (!cancelled) {
        setRawEntries(response.entries);
        setCursor(response.cursor);
        setHasMore(response.cursor !== null);
      }
    }).catch(() => {
      if (!cancelled) {
        setRawEntries([]);
        setHasMore(false);
        trackError('content_feed_load_failed');
      }
    }).finally(() => {
      if (!cancelled) {
        setIsFeedLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // fcpIds is memoized and only changes when sponsorships change.
    // locale triggers re-fetch when language changes.
  }, [isCommitmentsReady, fcpIds, locale]);
  const loadMore = useCallback(async () => {
    if (isFeedLoading || !hasMore) {
      return;
    }
    setIsFeedLoading(true);
    try {
      const response_0 = await fetchEntries({
        cursor
      });
      setRawEntries(prev => [...prev, ...response_0.entries]);
      setCursor(response_0.cursor);
      setHasMore(response_0.cursor !== null);
    } catch {
      trackError('content_feed_load_more_failed');
    } finally {
      setIsFeedLoading(false);
    }
  }, [cursor, fetchEntries, hasMore, isFeedLoading]);

  // Apply personalization filters then suppression pipeline
  const suppressionContext: SuppressionContext = useMemo(() => ({
    sourceToggles: enabledSources,
    inGoodStanding
  }), [enabledSources, inGoodStanding]);
  const {
    entries,
    shouldRender
  } = useMemo(() => {
    if (!suppressionEnabled) {
      return {
        entries: rawEntries,
        shouldRender: true
      };
    }

    // Personalization filters: recency then match validation
    const afterRecency = filterByRecency(rawEntries);
    const afterMatch = filterByMatch(afterRecency);

    // Suppression pipeline: source toggles, appeal standing, threshold
    return runSuppressionPipeline(afterMatch, suppressionContext);
  }, [rawEntries, suppressionContext, suppressionEnabled]);
  return {
    entries,
    isLoading,
    hasMore,
    loadMore,
    t,
    shouldRender,
    participantNameMap
  };
};